import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

import { fetchDailyAppointments } from '@/lib/services/appointmentService';
import { dateSource } from '@/lib/dateSource';
import { DateTime } from 'luxon';
import { getCompanyRegistry } from '@/lib/company/companyRegistrySingleton';

export interface DailyData {
  schedule: any; // Replace with proper type if available
  appointments: any; // Replace with proper type if available
}

interface CalendarContextType {
  currentDate: DateTime;
  dailyDataCache: Record<string, DailyData>;
  setDate: (date: DateTime) => void;
  getDataForDate: (date: DateTime) => Promise<DailyData>;
}

const CalendarContext = createContext<CalendarContextType | undefined>(undefined);

function formatDateKey(date: DateTime): string {
  return date.toISODate();          // keep salon/local zone
}

export const CalendarProvider = ({ children }: { children: ReactNode }) => {
  const [currentDate, setCurrentDate] = useState<DateTime>(dateSource.now());
  const [dailyDataCache, setDailyDataCache] = useState<Record<string, DailyData>>({});

  const setDate = (date: DateTime) => {
    setCurrentDate(date);
  };

  const getDataForDate = async (dateOrKey: DateTime | string): Promise<DailyData> => {
    let key: string;
    let date: DateTime;
    if (typeof dateOrKey === 'string') {
      key = dateOrKey;
      const tz = getCompanyRegistry().getTimezone?.() ?? "UTC";
      date = DateTime.fromISO(dateOrKey, { zone: tz }); // parse in salon zone
    } else {
      date = dateOrKey;
      key = formatDateKey(dateOrKey);
    }
    if (dailyDataCache[key]) {
      return dailyDataCache[key];
    }
    // Fetch data without using hooks directly in this function
    // Data should be fetched in a component or custom hook
    const appointments = await fetchDailyAppointments(date.toISODate());
    // Placeholder for schedule data; actual fetching should be handled in a component or custom hook
    // TODO: Refactor to fetch schedule data using React Query within a component or custom hook
    const schedule: any[] = []; // Temporarily typed as any[] to resolve TS error
    const data = { schedule, appointments };
    setDailyDataCache(prev => ({ ...prev, [key]: data })); // Fixed: was ({ .prev, [key]: data })
    return data;
  };

  // Preload data for currentDate on mount or when currentDate changes
  useEffect(() => {
    getDataForDate(currentDate);
    // Note: Schedule data fetching should be handled in a component or custom hook
  }, [currentDate]);

  return (
    <CalendarContext.Provider value={{ currentDate, dailyDataCache, setDate, getDataForDate }}>
      {children}
    </CalendarContext.Provider>
  );
};

export const useCalendar = (): CalendarContextType => {
  const context = useContext(CalendarContext);
  if (!context) {
    throw new Error('useCalendar must be used within a CalendarProvider');
  }
  return context;
};
